import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Sparkle, Brain, Copy, EnvelopeSimple,
    MapPin, Globe, User as UserIcon, Buildings, Phone,
    LinkedinLogo, InstagramLogo, MapTrifold, Palette, Code, ChartLine,
    Info, ChatCircleText, ArrowsClockwise, Calendar, CheckCircle, SealWarning,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmtDateTime, todayYmd } from "@/lib/dates";
import { FalseLeadDialog } from "@/components/FalseLeadDialog";

const STAGE_OPTIONS = ["new", "research_in_progress", "sent_to_crm", "dropped"];

export default function LeadDetail() {
    const { id } = useParams();
    const [lead, setLead] = useState(null);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [researching, setResearching] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const pollRef = useRef(null);
    const [emailing, setEmailing] = useState(false);
    const [tone, setTone] = useState("professional");
    const [angle, setAngle] = useState("");
    const [template, setTemplate] = useState("");
    const [refinePrompt, setRefinePrompt] = useState("");
    const [comment, setComment] = useState("");
    const [savingComment, setSavingComment] = useState(false);
    const [falseLeadOpen, setFalseLeadOpen] = useState(false);
    const [prevStage, setPrevStage] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get(`/leads/${id}`).then((r) => {
                setLead(r.data);
                if (r.data.insights?.enrichment_status === "pending") {
                    setVerifying(true);
                    pollEnrichment();
                }
            }),
            api.get(`/leads/${id}/activities`).then((r) => setActivities(r.data)),
        ])
            .catch(() => toast.error("Lead not found"))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => () => clearInterval(pollRef.current), []);

    const pollEnrichment = () => {
        clearInterval(pollRef.current);
        let tries = 0;
        pollRef.current = setInterval(async () => {
            tries += 1;
            try {
                const { data } = await api.get(`/leads/${id}`);
                setLead(data);
                const st = data.insights?.enrichment_status;
                if (st !== "pending" || tries >= 40) {
                    clearInterval(pollRef.current);
                    setVerifying(false);
                    if (st === "done") toast.success("Contacts verified via Apify");
                }
            } catch {
                clearInterval(pollRef.current);
                setVerifying(false);
            }
        }, 3000);
    };

    const research = async () => {
        setResearching(true);
        try {
            const { data } = await api.post(`/leads/${id}/research`);
            setLead(data);
            if (data.insights?.enrichment_status === "pending") {
                setVerifying(true);
                toast.success("AI research complete — verifying contacts in background…");
                pollEnrichment();
            } else {
                toast.success("AI research complete");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Research failed");
        } finally { setResearching(false); }
    };

    const generateEmail = async (refine = false) => {
        setEmailing(true);
        try {
            const body = { tone, angle, template };
            if (refine) {
                if (!refinePrompt.trim()) { toast.error("Add a refinement instruction"); setEmailing(false); return; }
                body.refine_prompt = refinePrompt;
                body.current_email = lead.generated_email || "";
            }
            const { data } = await api.post(`/leads/${id}/generate-email`, body);
            setLead(data);
            toast.success(refine ? "Email refined" : "Email generated");
            if (refine) setRefinePrompt("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Email generation failed");
        } finally { setEmailing(false); }
    };

    const updateStage = async (stage) => {
        if (stage === "dropped") {
            setPrevStage(lead.stage);
            setFalseLeadOpen(true);
            return;
        }
        const old = lead.stage;
        setLead({ ...lead, stage });
        try { await api.patch(`/leads/${id}/stage`, { stage }); toast.success(`Moved to ${stage.replace(/_/g, " ")}`); }
        catch { setLead({ ...lead, stage: old }); toast.error("Update failed"); }
    };

    const confirmFalseLead = async (reason) => {
        try {
            await api.patch(`/leads/${id}/stage`, { stage: "dropped", false_lead_reason: reason });
            setLead({ ...lead, stage: "dropped", false_lead_reason: reason });
            toast.success("Lead dropped — feedback saved");
        } catch { toast.error("Update failed"); }
        finally { setFalseLeadOpen(false); }
    };

    const updateNextAction = async (date) => {
        try {
            const { data } = await api.patch(`/leads/${id}`, { next_action_date: date });
            setLead(data);
        } catch { toast.error("Could not update date"); }
    };

    const addComment = async (e) => {
        e.preventDefault();
        if (!comment.trim()) return;
        setSavingComment(true);
        try {
            const { data } = await api.post(`/leads/${id}/activities`, { content: comment });
            setActivities([data, ...activities]);
            setComment("");
        } catch { toast.error("Could not save"); }
        finally { setSavingComment(false); }
    };

    if (loading) return <div className="text-muted-foreground text-sm">Loading lead…</div>;
    if (!lead) return <div className="text-muted-foreground text-sm">Lead not found.</div>;

    const hasInsights = !!lead.insights?.summary;

    return (
        <div className="space-y-6" data-testid="lead-detail-page">
            <Link to="/pipeline" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-pipeline">
                <ArrowLeft size={14} /> Back to pipeline
            </Link>

            {/* Header */}
            <div className="card-os p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="chip border-border bg-muted text-muted-foreground">
                                <Buildings size={11} /> {lead.industry}
                            </span>
                            <span className="overline text-muted-foreground">{lead.employee_count || "—"} employees</span>
                        </div>
                        <h1 className="font-heading text-4xl font-black leading-tight" data-testid="lead-company-name">
                            {lead.company_name}
                        </h1>
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> {lead.location || "—"}</span>
                            {lead.website && (
                                <a href={lead.website} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 hover:text-brand" data-testid="lead-website">
                                    <Globe size={14} /> {lead.website.replace(/^https?:\/\//, "")}
                                </a>
                            )}
                            {lead.contact_name && (
                                <span className="inline-flex items-center gap-1.5">
                                    <UserIcon size={14} /> {lead.contact_name} · {lead.contact_role}
                                </span>
                            )}
                        </div>
                        {lead.description && <p className="mt-3 text-sm text-foreground/80 max-w-3xl">{lead.description}</p>}
                        {lead.false_lead_reason && (
                            <div className="mt-3 chip border-signal-warning text-signal-warning bg-[#FFF9DB]">
                                Dropped: {lead.false_lead_reason}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col items-end gap-3">
                        <div>
                            <label className="overline text-muted-foreground">Stage</label>
                            <select data-testid="lead-stage-select" value={lead.stage}
                                onChange={(e) => updateStage(e.target.value)}
                                className="input-os mt-1 capitalize">
                                {STAGE_OPTIONS.map((s) => (
                                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="overline text-muted-foreground inline-flex items-center gap-1">
                                <Calendar size={11} /> Next action
                            </label>
                            <input
                                data-testid="next-action-date"
                                type="date"
                                min={todayYmd()}
                                value={lead.next_action_date || ""}
                                onChange={(e) => updateNextAction(e.target.value)}
                                className="input-os mt-1"
                            />
                        </div>
                        {lead.insights?.lead_score != null && (
                            <div className="text-right">
                                <div className="overline text-muted-foreground inline-flex items-center gap-1 group relative">
                                    Lead score
                                    <span className="cursor-help" data-testid="score-info">
                                        <Info size={11} />
                                    </span>
                                    <div className="hidden group-hover:block absolute right-0 top-full mt-1 z-20 w-56 bg-foreground text-white text-xs rounded-sm p-3 normal-case tracking-normal leading-relaxed">
                                        <div className="font-bold mb-1">Higher = better opportunity</div>
                                        <div>70–100 · Hot lead</div>
                                        <div>40–69 · Warm</div>
                                        <div>0–39 · Cold / poor fit</div>
                                    </div>
                                </div>
                                <div className="font-heading text-5xl font-black text-signal-ai leading-none mt-1"
                                    data-testid="lead-score-value">
                                    {lead.insights.lead_score}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* AI Panel */}
            <div className="bg-[#FFF4E6] border border-[#FFD8A8] rounded-sm p-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <Brain size={20} className="text-signal-ai" weight="fill" />
                        <div className="overline text-signal-ai">AI Research · service fit</div>
                    </div>
                    <button data-testid="run-research-btn" onClick={research} disabled={researching}
                        className="btn-ai disabled:opacity-60">
                        {researching ? (
                            <><span className="h-2 w-2 bg-white rounded-full animate-pulse-soft" />Analyzing…</>
                        ) : (
                            <><Sparkle size={16} weight="fill" />{hasInsights ? "Re-run AI Research" : "Run AI Research"}</>
                        )}
                    </button>
                </div>

                <div className="mt-3">
                    <span className="text-[11px] text-muted-foreground">
                        AI scores Branding / Website / SEO fit and fills empty contact fields with AI estimates (Unverified), then verifies email & socials from the website via Apify in the background. Verified contacts come from Apify only.
                    </span>
                </div>

                {researching && !hasInsights && (
                    <div className="mt-5 space-y-2">
                        {["Scanning public signals…", "Scoring branding fit…", "Scoring website + SEO opportunity…"].map((t) => (
                            <div key={t} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <span className="h-1.5 w-1.5 bg-signal-ai rounded-full animate-pulse-soft" />{t}
                            </div>
                        ))}
                    </div>
                )}

                {hasInsights && (
                    <div className="mt-5 space-y-6">
                        <div>
                            <div className="overline text-muted-foreground">Executive Summary</div>
                            <p className="mt-2 text-sm text-foreground" data-testid="ai-summary">{lead.insights.summary}</p>
                            {lead.insights.score_reasoning && (
                                <div className="mt-3">
                                    <div className="overline text-muted-foreground">Why this score</div>
                                    <p className="mt-1 text-sm text-foreground/80">{lead.insights.score_reasoning}</p>
                                </div>
                            )}
                        </div>

                        <div className="grid md:grid-cols-3 gap-3" data-testid="ai-services">
                            <ServiceCard icon={<Palette size={18} />} label="Branding" data={lead.insights.branding} testid="service-branding" />
                            <ServiceCard icon={<Code size={18} />} label="Website" data={lead.insights.website} testid="service-website" />
                            <ServiceCard icon={<ChartLine size={18} />} label="SEO" data={lead.insights.seo} testid="service-seo" />
                        </div>
                    </div>
                )}

                {lead.insights?.contact && (() => {
                            const verifiedSet = new Set(lead.insights.contact_verified || []);
                            return (
                            <div className="mt-6">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div className="overline text-muted-foreground">Contact & Channels</div>
                                    <div className="flex items-center gap-3 text-[11px]">
                                        {verifying && (
                                            <span data-testid="verifying-indicator" className="inline-flex items-center gap-1 text-brand">
                                                <span className="h-2 w-2 bg-brand rounded-full animate-pulse-soft" /> Verifying contacts…
                                            </span>
                                        )}
                                        <span className="inline-flex items-center gap-1 text-signal-success"><CheckCircle size={13} weight="fill" /> Verified (Apify)</span>
                                        <span className="inline-flex items-center gap-1 text-amber-600"><SealWarning size={13} weight="fill" /> Unverified (AI — double-check)</span>
                                    </div>
                                </div>
                                <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="ai-contact">
                                    <ContactLink icon={<Globe size={14} />} label="Website" value={lead.insights.contact.website} href={lead.insights.contact.website} verified={verifiedSet.has("website")} testid="contact-website" />
                                    <ContactLink icon={<LinkedinLogo size={14} />} label="LinkedIn" value={lead.insights.contact.linkedin} href={lead.insights.contact.linkedin} verified={verifiedSet.has("linkedin")} testid="contact-linkedin" />
                                    <ContactLink icon={<InstagramLogo size={14} />} label="Instagram" value={lead.insights.contact.instagram} href={lead.insights.contact.instagram} verified={verifiedSet.has("instagram")} testid="contact-instagram" />
                                    <ContactLink icon={<MapTrifold size={14} />} label="Google Maps" value={lead.insights.contact.google_maps} href={lead.insights.contact.google_maps} verified={verifiedSet.has("google_maps")} testid="contact-maps" />
                                    <ContactLink icon={<EnvelopeSimple size={14} />} label="Email" value={lead.insights.contact.email} href={lead.insights.contact.email ? `mailto:${lead.insights.contact.email}` : null} verified={verifiedSet.has("email")} testid="contact-email" />
                                    <ContactLink icon={<Phone size={14} />} label="Phone" value={lead.insights.contact.phone} href={lead.insights.contact.phone ? `tel:${lead.insights.contact.phone}` : null} verified={verifiedSet.has("phone")} testid="contact-phone" />
                                </div>
                            </div>
                            );
                        })()}
            </div>

            {/* Outreach */}
            <div className="card-os p-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="overline text-muted-foreground">AI Outreach</div>
                        <h2 className="font-heading text-2xl font-bold mt-1">Personalized email</h2>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-3">
                    <div>
                        <label className="overline text-muted-foreground">Tone</label>
                        <select data-testid="email-tone" value={tone} onChange={(e) => setTone(e.target.value)} className="input-os mt-1">
                            <option value="professional">Professional</option>
                            <option value="casual">Casual</option>
                            <option value="bold">Bold</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="overline text-muted-foreground">Angle / CTA (optional)</label>
                        <input data-testid="email-angle" value={angle} onChange={(e) => setAngle(e.target.value)}
                            className="input-os mt-1" placeholder="e.g. Offer a free SEO audit" />
                    </div>
                </div>

                <div className="mt-3">
                    <label className="overline text-muted-foreground">Email personalization prompt (optional)</label>
                    <textarea data-testid="email-template" value={template} onChange={(e) => setTemplate(e.target.value)}
                        rows={4} className="input-os mt-1 font-mono text-xs resize-none"
                        placeholder={`Any of: a base template (with {first_name}, {company}, {opportunity} placeholders), a previous conversation you had with the lead (to write a follow-up), or specific instructions for tone/content.`} />
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Paste a template, a past conversation for a follow-up, or any personalization instructions.
                    </p>
                </div>

                <div className="mt-4 flex justify-end">
                    <button data-testid="generate-email-btn" onClick={() => generateEmail(false)} disabled={emailing}
                        className="btn-ai disabled:opacity-60">
                        {emailing && !refinePrompt ? (
                            <><span className="h-2 w-2 bg-white rounded-full animate-pulse-soft" />Drafting with AI…</>
                        ) : (
                            <><EnvelopeSimple size={16} />{lead.generated_email ? "Regenerate Email" : "Generate Outreach Email"}</>
                        )}
                    </button>
                </div>

                {lead.generated_email && (
                    <>
                        <div className="mt-5 border border-border rounded-sm bg-muted/40 p-4 relative" data-testid="generated-email">
                            <button data-testid="copy-email-btn"
                                onClick={() => { navigator.clipboard.writeText(lead.generated_email); toast.success("Copied"); }}
                                className="absolute top-3 right-3 btn-secondary text-xs inline-flex items-center gap-1 py-1 px-2">
                                <Copy size={12} /> Copy
                            </button>
                            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed pr-16">{lead.generated_email}</pre>
                        </div>

                        <div className="mt-4 border border-border rounded-sm p-3 bg-white">
                            <label className="overline text-muted-foreground">Refine this email</label>
                            <div className="flex gap-2 mt-1">
                                <input data-testid="refine-prompt" value={refinePrompt} onChange={(e) => setRefinePrompt(e.target.value)}
                                    className="input-os flex-1"
                                    placeholder="e.g. Make it shorter, mention their recent product launch, more casual tone" />
                                <button data-testid="refine-btn" onClick={() => generateEmail(true)} disabled={emailing}
                                    className="btn-secondary inline-flex items-center gap-1 disabled:opacity-60 whitespace-nowrap">
                                    <ArrowsClockwise size={14} /> Refine
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Activity / Comments */}
            <div className="card-os p-6">
                <div className="flex items-center gap-2 mb-4">
                    <ChatCircleText size={18} />
                    <div>
                        <div className="overline text-muted-foreground">Activity</div>
                        <h2 className="font-heading text-2xl font-bold mt-1">Comments & log</h2>
                    </div>
                </div>

                <form onSubmit={addComment} className="flex gap-2">
                    <input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                        className="input-os flex-1"
                        placeholder="e.g. Sent intro email — no reply yet. Following up in 3 days." />
                    <button type="submit" data-testid="comment-submit" disabled={savingComment || !comment.trim()}
                        className="btn-primary disabled:opacity-60 whitespace-nowrap">
                        {savingComment ? "Saving…" : "Add Entry"}
                    </button>
                </form>

                <div className="mt-5 space-y-3" data-testid="activity-list">
                    {activities.length === 0 && (
                        <div className="text-sm text-muted-foreground text-center py-6">
                            No entries yet. Log calls, emails, follow-ups — anything.
                        </div>
                    )}
                    {activities.map((a) => (
                        <div key={a.id} className="border-l-2 border-border pl-3 py-1" data-testid={`activity-${a.id}`}>
                            <div className="text-sm text-foreground">{a.content}</div>
                            <div className="text-[11px] text-muted-foreground mt-1">
                                {a.author_name} · {fmtDateTime(a.created_at)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <FalseLeadDialog
                open={falseLeadOpen}
                onClose={() => { setFalseLeadOpen(false); setPrevStage(null); }}
                onConfirm={confirmFalseLead}
            />
        </div>
    );
}

function scoreColor(score) {
    if (score == null) return "text-muted-foreground";
    if (score >= 70) return "text-signal-success";
    if (score >= 40) return "text-signal-ai";
    return "text-muted-foreground";
}

function ServiceCard({ icon, label, data, testid }) {
    const score = data?.score;
    return (
        <div className="border border-border rounded-sm p-4 bg-white" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="text-signal-ai">{icon}</span>
                    <span className="font-heading font-bold text-sm uppercase tracking-wider">{label}</span>
                </div>
                <span className={`font-heading text-2xl font-black leading-none ${scoreColor(score)}`}
                    data-testid={`${testid}-score`}>{score ?? "—"}</span>
            </div>
            {data?.reasoning && <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{data.reasoning}</p>}
            {data?.angle && (
                <div className="mt-3 pt-3 border-t border-border">
                    <div className="overline text-muted-foreground">Pitch angle</div>
                    <p className="text-xs text-foreground/90 mt-1">{data.angle}</p>
                </div>
            )}
        </div>
    );
}

function ContactLink({ icon, label, value, href, verified, testid }) {
    const empty = !value;
    const Wrap = href ? "a" : "div";
    const wrapProps = href ? { href, target: "_blank", rel: "noreferrer" } : {};
    return (
        <Wrap {...wrapProps} data-testid={testid}
            className={`flex items-center gap-2 border rounded-sm px-3 py-2 bg-white text-sm ${
                empty ? "opacity-50 border-border"
                    : verified ? "border-signal-success/40 hover:border-signal-success transition-colors"
                    : "border-amber-300 hover:border-amber-500 transition-colors"
            }`}>
            <span className="text-muted-foreground">{icon}</span>
            <div className="min-w-0 flex-1">
                <div className="overline text-muted-foreground text-[9px]">{label}</div>
                <div className="truncate text-xs">{value || "—"}</div>
            </div>
            {!empty && (
                verified ? (
                    <span data-testid={`${testid}-verified`} title="Verified via Apify (Google Maps)" className="text-signal-success shrink-0">
                        <CheckCircle size={16} weight="fill" />
                    </span>
                ) : (
                    <span data-testid={`${testid}-unverified`} title="AI-sourced — not verified. Please double-check." className="text-amber-500 shrink-0">
                        <SealWarning size={16} weight="fill" />
                    </span>
                )
            )}
        </Wrap>
    );
}
